import { Box, Button, Paper, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/theme/Icon';

export interface DomainStepProps {
  n: number;
  title: string;
  subtitle?: string;
  open: boolean;
  done: boolean;
  onChange?: () => void;
  disabled?: boolean;
  children?: ReactNode;
}

export function DomainStep({
  n,
  title,
  subtitle,
  open,
  done,
  onChange,
  disabled,
  children,
}: DomainStepProps) {
  const { t } = useTranslation();
  // A step not yet reached reads as secondary text, never a faded card (A11Y-004).
  const reached = open || done;
  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 3,
          py: 2,
          borderBottom: open ? 1 : 0,
          borderColor: 'divider',
        }}
      >
        <Box
          aria-hidden
          sx={{
            width: 26,
            height: 26,
            flex: '0 0 26px',
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            typography: 'body2',
            fontWeight: 500,
            bgcolor: done || open ? 'primary.main' : 'action.selected',
            color: done || open ? 'primary.contrastText' : 'text.secondary',
          }}
        >
          {done ? <Icon name="check" size="sm" /> : n}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            component="h2"
            sx={reached ? undefined : { color: 'text.secondary' }}
          >
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        {done && onChange && !disabled ? (
          <Button size="small" onClick={onChange}>
            {t('domain.change')}
          </Button>
        ) : null}
      </Box>
      {open ? <Box sx={{ px: 3, py: 2.5 }}>{children}</Box> : null}
    </Paper>
  );
}
